import { apiFetch } from './client';

/**
 * TIENDA — productos, inventario, ventas y finanzas.
 *
 * Tres prefijos, y la separación no es cosmética:
 *   /api/productos  → módulo `catalogo`
 *   /api/inventario → módulo `inventario_variantes`
 *   /api/tienda/*   → módulos `ventas` y `finanzas`
 *
 * `/api/ventas` (sin `tienda`) es de ELECTRO MEDIA y no se toca desde aquí.
 */

// ─── Tipos ─────────────────────────────────────────────────────────────────

export interface Variante {
  id: string;
  sku: string | null;
  label: string;
  attributesJson: Record<string, string>;
  price: number | null;
  cost: number | null;
  stock: number;
  lowStockThreshold: number;
  isActive: boolean;
}

export interface Producto {
  id: string;
  name: string;
  sku: string | null;
  category: string | null;
  description: string | null;
  price: number;
  cost: number | null;
  aliasesJson: string[];
  isActive: boolean;
  /** Agotada, ¿la tienda web la ofrece «por encargo»? */
  aceptaEncargo: boolean;
  /** ¿Lleva ITBIS? Solo cuenta si el negocio factura con ITBIS. */
  aplicaItbis: boolean;
  variants: Variante[];
  /**
   * CLAVES de las fotos, no URLs. La URL depende de dónde esté servido el
   * archivo hoy; la clave no. Se arma con `urlDeFoto`.
   */
  imagesJson: string[];
}

/**
 * MERMA es un tipo aparte de SALIDA a proposito: lo que se rompio o se perdio
 * no es lo mismo que lo que se vendio, y juntarlos inflaria el «vendido» con
 * mercancia que nadie pago.
 */
export type TipoDeMovimiento = 'ENTRADA' | 'SALIDA' | 'AJUSTE' | 'MERMA';

export interface Movimiento {
  id: string;
  tipo: TipoDeMovimiento;
  cantidad: number;
  motivo: string | null;
  saleId: string | null;
  createdAt: string;
  productVariant: Variante & { product: { id: string; name: string; sku: string | null } };
}

export type EstadoDeVenta = 'BORRADOR' | 'CONFIRMADA' | 'ANULADA';

export interface LineaDeVenta {
  id: string;
  cantidad: number;
  unitPrice: number;
  /** Rebaja de ESTA pieza, en plata. */
  descuento: number;
  unitCost: number | null;
  productVariantId: string;
  productVariant: Variante & { product: { id: string; name: string; sku: string | null } };
}

export interface Venta {
  id: string;
  numero: string;
  estado: EstadoDeVenta;
  total: number;
  costoTotal: number;
  /** Rebaja sobre el total, en plata. */
  descuento: number;
  /** Lo que cobra el mensajero. Suma al total y NO al margen. */
  envio: number;
  formaDePago: string | null;
  /**
   * ¿Entró la plata? Una contra entrega confirmada ya descontó inventario
   * pero NO está cobrada hasta que vuelve el mensajero.
   */
  cobrado: boolean;
  cobradoAt: string | null;
  direccion: string | null;
  clienteId: string | null;
  /** El chat del que salió la venta, cuando se pasó desde un chat. */
  conversationId?: string | null;
  cliente?: { id: string; nombre: string; telefono?: string | null; documento?: string | null } | null;
  /** La factura de esta venta, si ya se emitió. Una venta lleva una sola. */
  factura?: { id: string; numero: string } | null;
  /** De qué lugar sale la mercancía. Vacío en las ventas viejas: salieron del total. */
  ubicacionId?: string | null;
  ubicacion?: { id: string; nombre: string; tipo: 'ALMACEN' | 'TIENDA' } | null;
  notas: string | null;
  createdAt: string;
  confirmedAt: string | null;
  lineas: LineaDeVenta[];
  /**
   * CUÁNTA PLATA VOLVIÓ de esta venta. `total` NO baja al devolver: quedó
   * congelado al confirmar y el libro es la verdad.
   */
  devuelto?: number;
  /**
   * Lo que queda en pie: `total − devuelto`. Es lo que de verdad se cobra.
   * Sumar `total` diría de más, y el mensajero iría a cobrar lo que no es.
   * En una confirmada con pagos, es el SALDO: lo que falta después del anticipo.
   */
  porCobrar?: number;
  /** Lo que ya se pagó (anticipo, saldo…). */
  pagado?: number;
  /** Lo que falta por pagar. */
  saldo?: number;
  estadoDePago?: EstadoDePago;
  /** Solo en el detalle. */
  pagos?: PagoDeVenta[];
  /** "tienda" (retira) | "mensajero" | "transportista". */
  envioModo?: ModoDeEnvio | null;
  envioEmpresa?: string | null;
  envioDestino?: string | null;
  /** Lo que cuesta de verdad (`envio` es lo que se le cobra al cliente). */
  costoMensajero?: number;
  costoTransportista?: number;
  /** Cuándo se la llevó. null = por entregar. */
  entregadaAt?: string | null;
}

export type EstadoDePago = 'sin_pago' | 'anticipo' | 'pagada';
export type ModoDeEnvio = 'tienda' | 'mensajero' | 'transportista';
export interface PagoDeVenta {
  id: string;
  monto: number;
  forma: string | null;
  nota: string | null;
  createdAt: string;
}
export interface DatosDeEnvio {
  envioModo?: ModoDeEnvio | null;
  envioEmpresa?: string | null;
  envioDestino?: string | null;
  costoMensajero?: number;
  costoTransportista?: number;
  direccion?: string | null;
}

export function registrarPago(id: string, body: { monto: number; forma?: string | null; nota?: string | null }) {
  return apiFetch<{ saldo: number }>(`/api/tienda/ventas/${id}/pagos`, { method: 'POST', body });
}
export function quitarPago(id: string, pagoId: string) {
  return apiFetch<{ quitado: boolean }>(`/api/tienda/ventas/${id}/pagos/${pagoId}`, { method: 'DELETE' });
}
export function marcarEntregada(id: string, entregada = true) {
  return apiFetch<{ entregada: boolean }>(`/api/tienda/ventas/${id}/entregar`, { method: 'POST', body: { entregada } });
}
export function guardarEnvio(id: string, body: DatosDeEnvio) {
  return apiFetch<{ guardado: boolean }>(`/api/tienda/ventas/${id}/envio`, { method: 'PATCH', body });
}

/**
 * LA VENTA COMO VIENE EN UNA LISTA — con MENOS datos, y por eso es otro tipo.
 *
 * `GET /api/tienda/ventas` no trae el precio ni el costo de cada línea: son
 * doscientas ventas y esas columnas no se enseñan en la tabla. `GET
 * /api/tienda/ventas/:id` sí los trae.
 *
 * Estaban tipados como el MISMO `Venta`, y por eso el comprobante enseñó
 * «RD$NaN»: recibía la fila de la tabla y multiplicaba un `unitPrice` que no
 * existía. TypeScript no podía verlo, porque el tipo decía que sí estaba.
 *
 * Dos formas distintas necesitan dos tipos distintos. Si hace falta el precio,
 * hay que pedir la venta completa — y ahora el compilador lo exige.
 */
export type LineaEnLista = Pick<LineaDeVenta, 'id' | 'cantidad' | 'productVariant'>;

export type VentaEnLista = Omit<Venta, 'lineas'> & { lineas: LineaEnLista[] };

export interface TopVariante {
  productVariantId: string;
  producto: string;
  sku: string | null;
  talla: string;
  cantidad: number;
  total: number;
}

export interface ResumenDeVentas {
  desde: string;
  hasta: string;
  ventas: number;
  totalVendido: number;
  costoConocido: number;
  margen: number;
  lineasSinCosto: number;
  ticketPromedio: number | null;
  topVariantes: TopVariante[];
  /** Piezas que volvieron. Ya están RESTADAS de todo lo de arriba. */
  piezasDevueltas: number;
  montoDevuelto: number;
}

export interface CuantoQueda {
  desde: string;
  hasta: string;
  totalVendido: number;
  costoDeLoVendido: number;
  margenBruto: number;
  /** Lo fijo del mes, prorrateado por los dias del periodo. */
  gastosFijosDelPeriodo: number;
  /** Lo variable, ENTERO: la compra de una paca no se prorratea. */
  gastosVariablesDelPeriodo: number;
  gastosDelPeriodo: number;
  /** margenBruto − gastosDelPeriodo. Puede ser negativo: se muestra igual. */
  quedaLimpio: number;
  lineasSinCosto: number;
  puntoDeEquilibrio: number | null;
}

export interface ValorDelInventario {
  piezas: number;
  valorAlCosto: number;
  variantesSinCosto: number;
}

export type Periodo = 'hoy' | 'semana' | 'mes';

// ─── Productos ─────────────────────────────────────────────────────────────

export function listarProductos(query: {
  buscar?: string;
  categoria?: string;
  limit?: number;
}): Promise<Producto[]> {
  return apiFetch<Producto[]>('/api/productos', { query });
}

export interface VarianteNueva {
  atributos: Record<string, string>;
  price?: number | null;
  cost?: number | null;
  stockInicial?: number;
  lowStockThreshold?: number;
}

export function crearProducto(body: {
  name: string;
  sku: string;
  category?: string;
  description?: string;
  price: number;
  cost?: number | null;
  aliases?: string[];
  variantes?: VarianteNueva[];
}): Promise<Producto> {
  return apiFetch<Producto>('/api/productos', { method: 'POST', body });
}

export function actualizarProducto(
  id: string,
  body: Partial<{
    name: string;
    sku: string;
    category: string;
    description: string;
    price: number;
    cost: number | null;
    aliases: string[];
    aceptaEncargo: boolean;
    aplicaItbis: boolean;
    isActive: boolean;
  }>,
): Promise<Producto> {
  return apiFetch<Producto>(`/api/productos/${id}`, { method: 'PATCH', body });
}

export function crearVariante(productId: string, body: VarianteNueva): Promise<Variante> {
  return apiFetch<Variante>(`/api/productos/${productId}/variantes`, { method: 'POST', body });
}

export function actualizarVariante(
  varianteId: string,
  body: Partial<{ price: number | null; cost: number | null; lowStockThreshold: number }>,
): Promise<Variante> {
  return apiFetch<Variante>(`/api/productos/variantes/${varianteId}`, { method: 'PATCH', body });
}

// ─── Inventario ────────────────────────────────────────────────────────────

export function listarMovimientos(query: {
  productVariantId?: string;
  tipo?: TipoDeMovimiento;
  limit?: number;
}): Promise<Movimiento[]> {
  return apiFetch<Movimiento[]>('/api/inventario/movimientos', { query });
}

export function registrarMovimiento(body: {
  productVariantId: string;
  tipo: TipoDeMovimiento;
  cantidad: number;
  motivo?: string;
}): Promise<Movimiento> {
  return apiFetch<Movimiento>('/api/inventario/movimientos', { method: 'POST', body });
}

// ─── Almacén y tiendas ─────────────────────────────────────────────────────

export interface Ubicacion {
  id: string;
  nombre: string;
  tipo: 'ALMACEN' | 'TIENDA';
}

export interface ExistenciaPorUbicacion {
  id: string;
  label: string;
  sku: string | null;
  producto: { id: string; name: string; sku: string | null };
  total: number;
  enAlmacen: number;
  /** Por id de tienda. La que no aparece tiene cero. */
  porTienda: Record<string, number>;
}

export interface Traslado {
  id: string;
  cantidad: number;
  sacadoPor: string;
  /** Quién confirmó en la tienda que llegó. `null` = por confirmar. */
  recibidoPor: string | null;
  recibidoAt: string | null;
  nota: string | null;
  createdAt: string;
  origen: { id: string; nombre: string };
  /** `null` = venta directa. */
  destino: { id: string; nombre: string } | null;
  productVariant: { id: string; label: string; product: { id: string; name: string } };
}

export function listarUbicaciones(): Promise<Ubicacion[]> {
  return apiFetch<Ubicacion[]>('/api/inventario/ubicaciones');
}

export function crearUbicacion(nombre: string): Promise<Ubicacion> {
  return apiFetch<Ubicacion>('/api/inventario/ubicaciones', { method: 'POST', body: { nombre } });
}

export function existenciasPorUbicacion(): Promise<ExistenciaPorUbicacion[]> {
  return apiFetch<ExistenciaPorUbicacion[]>('/api/inventario/por-ubicacion');
}

/** El conteo: cuántas hay de verdad en cada lugar (id → piezas). Cada diferencia queda en el libro. */
/** ¿El asistente de cada sucursal ofrece lo que hay en otra tienda o en el almacén? */
export function agenteOtrasTiendas(): Promise<{ encendido: boolean }> {
  return apiFetch('/api/inventario/agente-otras-tiendas');
}

export function ponerAgenteOtrasTiendas(encendido: boolean): Promise<{ encendido: boolean }> {
  return apiFetch('/api/inventario/agente-otras-tiendas', { method: 'PUT', body: { encendido } });
}

export function contarEnLugares(body: {
  productVariantId: string;
  cantidades: Record<string, number>;
  nota?: string | null;
}): Promise<{ cambios: Array<{ ubicacionId: string; lugar: string; habia: number; hay: number }> }> {
  return apiFetch('/api/inventario/conteo', { method: 'POST', body });
}

export function listarTraslados(): Promise<Traslado[]> {
  return apiFetch<Traslado[]>('/api/inventario/traslados');
}

/** La tienda dice que le llegó. */
export function confirmarTraslado(id: string, recibidoPor: string): Promise<Traslado> {
  return apiFetch<Traslado>(`/api/inventario/traslados/${id}/recibido`, { method: 'POST', body: { recibidoPor } });
}

export function trasladar(body: {
  productVariantId: string;
  cantidad: number;
  origenId: string;
  destinoId: string | null;
  sacadoPor: string;
  nota?: string | null;
}): Promise<Traslado> {
  return apiFetch<Traslado>('/api/inventario/traslados', { method: 'POST', body });
}

export function bajoStock(): Promise<Array<Variante & { product: Producto }>> {
  return apiFetch<Array<Variante & { product: Producto }>>('/api/inventario/bajo-stock');
}

export function auditarSaldo(
  varianteId: string,
): Promise<{ segunElLibro: number; segunElSaldo: number; cuadra: boolean }> {
  return apiFetch(`/api/inventario/auditar/${varianteId}`);
}

// ─── Ventas ────────────────────────────────────────────────────────────────

export function listarVentas(query: {
  estado?: EstadoDeVenta;
  cobrado?: boolean;
  clienteId?: string;
  limit?: number;
}): Promise<VentaEnLista[]> {
  return apiFetch<VentaEnLista[]>('/api/tienda/ventas', { query });
}

export function leerVenta(id: string): Promise<Venta> {
  return apiFetch<Venta>(`/api/tienda/ventas/${id}`);
}

export function crearVenta(body: {
  lineas: Array<{
    productVariantId: string;
    cantidad: number;
    unitPrice?: number;
    descuento?: number;
  }>;
  clienteId?: string | null;
  formaDePago?: string | null;
  descuento?: number;
  envio?: number;
  direccion?: string | null;
  notas?: string | null;
  /** De qué lugar sale (almacén o una tienda). */
  ubicacionId?: string | null;
}): Promise<Venta> {
  return apiFetch<Venta>('/api/tienda/ventas', { method: 'POST', body });
}

/** Cambia de qué lugar sale un borrador. */
export function ponerLugarDeVenta(id: string, ubicacionId: string | null): Promise<Venta> {
  return apiFetch<Venta>(`/api/tienda/ventas/${id}/lugar`, { method: 'PATCH', body: { ubicacionId } });
}

export function confirmarVenta(id: string): Promise<Venta> {
  return apiFetch<Venta>(`/api/tienda/ventas/${id}/confirmar`, { method: 'POST' });
}

export function anularVenta(id: string, motivo: string): Promise<Venta> {
  return apiFetch<Venta>(`/api/tienda/ventas/${id}/anular`, { method: 'POST', body: { motivo } });
}

// ─── Reportes y finanzas ───────────────────────────────────────────────────

/**
 * El rango explícito manda sobre el nombre del período. Lo usa la comparación
 * contra el período anterior, que no tiene nombre: es un rango y ya.
 */
export interface Rango {
  desde?: string;
  hasta?: string;
}

export function resumen(periodo: Periodo, rango: Rango = {}): Promise<ResumenDeVentas> {
  return apiFetch<ResumenDeVentas>('/api/tienda/resumen', { query: { periodo, ...rango } });
}

export function estancados(dias: number): Promise<Array<Variante & { product: Producto }>> {
  return apiFetch<Array<Variante & { product: Producto }>>('/api/tienda/estancados', {
    query: { dias },
  });
}

export function cuantoQueda(periodo: Periodo, rango: Rango = {}): Promise<CuantoQueda> {
  return apiFetch<CuantoQueda>('/api/tienda/cuanto-queda', { query: { periodo, ...rango } });
}

export function valorDelInventario(): Promise<ValorDelInventario> {
  return apiFetch<ValorDelInventario>('/api/tienda/valor-inventario');
}

// ─── Importador de inventario ──────────────────────────────────────────────

export interface FilaMala {
  linea: number;
  contenido: string;
  motivo: string;
}

export interface ProductoAImportar {
  codigo: string;
  nombre: string;
  categoria: string | null;
  precio: number;
  costo: number | null;
  alias: string[];
  yaExiste: boolean;
  /** `lugar`: la tienda a la que entra; `null` = el almacén. */
  variantes: Array<{ talla: string; cantidad: number; aviso: number; yaExiste: boolean; lugar: string | null }>;
}

export interface Revision {
  productos: ProductoAImportar[];
  malas: FilaMala[];
  resumen: {
    filasLeidas: number;
    productosNuevos: number;
    productosQueYaEstaban: number;
    variantesNuevas: number;
    piezas: number;
    sinCosto: number;
  };
}

export interface ResultadoImportacion {
  productosCreados: number;
  productosActualizados: number;
  variantesCreadas: number;
  piezasIngresadas: number;
  problemas: Array<{ codigo: string; motivo: string }>;
}

/** PASO 1: no guarda nada. Solo dice qué pasaría. */
export function revisarImportacion(texto: string): Promise<Revision> {
  return apiFetch<Revision>('/api/productos/importar/revisar', { method: 'POST', body: { texto } });
}

/** PASO 2: escribe. */
export function importarInventario(texto: string, motivo?: string): Promise<ResultadoImportacion> {
  return apiFetch<ResultadoImportacion>('/api/productos/importar', {
    method: 'POST',
    body: { texto, motivo },
  });
}

export function plantillaImportacion(): Promise<{ csv: string }> {
  return apiFetch<{ csv: string }>('/api/productos/importar/plantilla');
}

// ─── Devoluciones ──────────────────────────────────────────────────────────

export interface EstadoDeDevolucion {
  productVariantId: string;
  talla: string;
  producto: string;
  vendido: number;
  devuelto: number;
  sePuedeDevolver: number;
}

export interface DevolucionPorTalla {
  producto: string;
  sku: string | null;
  talla: string;
  vendidas: number;
  devueltas: number;
  tasa: number | null;
}

export function devolvible(saleId: string): Promise<EstadoDeDevolucion[]> {
  return apiFetch<EstadoDeDevolucion[]>(`/api/tienda/ventas/${saleId}/devolvible`);
}

export function devolver(
  saleId: string,
  lineas: Array<{ productVariantId: string; cantidad: number }>,
  motivo: string,
): Promise<{ numero: string; lineas: number }> {
  return apiFetch(`/api/tienda/ventas/${saleId}/devolver`, {
    method: 'POST',
    body: { lineas, motivo },
  });
}

export function devolucionesPorTalla(
  periodo: Periodo,
  rango: Rango = {},
): Promise<DevolucionPorTalla[]> {
  return apiFetch<DevolucionPorTalla[]>('/api/tienda/devoluciones', {
    query: { periodo, ...rango },
  });
}

// ─── Cliente, suplidor, paca, cobro y bitácora ─────────────────────────────

export interface Cliente {
  id: string;
  nombre: string;
  telefono: string | null;
  direccion: string | null;
  zona: string | null;
  notas: string | null;
  contactId: string | null;
  /** RNC o cédula, en dígitos. */
  documento: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface FichaDeCliente {
  cliente: Cliente;
  ventas: VentaEnLista[];
  resumen: {
    compras: number;
    /** Ya neto de lo devuelto. */
    totalComprado: number;
    /** Cuántas veces devolvió. Dice si esta clienta devuelve mucho. */
    devoluciones: number;
    /** Cuánta plata volvió. */
    montoDevuelto: number;
    /** Ya neto: lo que de verdad te debe. */
    porCobrar: number;
    ultimaCompra: string | null;
  };
}

export interface Suplidor {
  id: string;
  nombre: string;
  telefono: string | null;
  notas: string | null;
  isActive: boolean;
}

export interface Paca {
  id: string;
  nombre: string;
  suplidorId: string | null;
  suplidor?: { id: string; nombre: string } | null;
  fecha: string;
  costoTotal: number;
  piezasDeclaradas: number | null;
  notas: string | null;
  cerradaAt: string | null;
  _count?: { productos: number };
}

export interface Reparto {
  paca: { id: string; nombre: string; costoTotal: number };
  piezasTotales: number;
  faltanPiezas: number | null;
  sobrante: number;
  productos: Array<{
    productoId: string;
    nombre: string;
    sku: string | null;
    piezas: number;
    peso: number;
    costoPorPieza: number;
    costoActual: number | null;
  }>;
}

export interface RendimientoDePaca {
  pacaId: string;
  nombre: string;
  suplidor: string | null;
  invertido: number;
  piezas: number;
  piezasVendidas: number;
  vendido: number;
  costoDeLoVendido: number;
  margen: number;
  /** Piezas vendidas sin costo conocido. El margen NO las cuenta. */
  piezasSinCosto: number;
  porcentajeVendido: number;
}

export type TipoDeGasto = 'FIJO' | 'VARIABLE';

export interface Gasto {
  id: string;
  concepto: string;
  tipo: TipoDeGasto;
  categoria: string | null;
  monto: number;
  fecha: string;
  pagado: boolean;
  metodo: string | null;
  pacaId: string | null;
  /** ¿Lleva ITBIS que se resta del cobrado? */
  aplicaItbis: boolean;
  isActive: boolean;
}

export interface PropuestaDelAgente {
  id: string;
  que: 'anular_venta' | 'devolver' | 'ajustar_existencia' | 'marcar_cobrada';
  referencia: string | null;
  detalle: string | null;
  porque: string;
  conversationId: string | null;
  creada: string;
  estado: 'pendiente' | 'aprobada' | 'descartada';
}

export interface EntradaDeBitacora {
  id: string;
  accion: string;
  entidad: string;
  entidadId: string;
  porElAgente: boolean;
  usuario: { id: string; name: string | null } | null;
  detalle: Record<string, unknown>;
  cuando: string;
}

export function listarClientes(query: { buscar?: string; limit?: number }): Promise<Cliente[]> {
  return apiFetch<Cliente[]>('/api/tienda/clientes', { query });
}
export function fichaDeCliente(id: string): Promise<FichaDeCliente> {
  return apiFetch<FichaDeCliente>(`/api/tienda/clientes/${id}/ficha`);
}
export function crearCliente(body: Partial<Cliente> & { nombre: string }): Promise<Cliente> {
  return apiFetch<Cliente>('/api/tienda/clientes', { method: 'POST', body });
}
export function actualizarCliente(id: string, body: Partial<Cliente>): Promise<Cliente> {
  return apiFetch<Cliente>(`/api/tienda/clientes/${id}`, { method: 'PATCH', body });
}

export function listarSuplidores(): Promise<Suplidor[]> {
  return apiFetch<Suplidor[]>('/api/tienda/suplidores');
}
export function crearSuplidor(body: { nombre: string; telefono?: string | null }): Promise<Suplidor> {
  return apiFetch<Suplidor>('/api/tienda/suplidores', { method: 'POST', body });
}

export function listarPacas(): Promise<Paca[]> {
  return apiFetch<Paca[]>('/api/tienda/pacas');
}
export function leerPaca(id: string): Promise<Paca & { productos: Producto[] }> {
  return apiFetch(`/api/tienda/pacas/${id}`);
}
export function crearPaca(body: {
  nombre: string;
  suplidorId?: string | null;
  costoTotal: number;
  piezasDeclaradas?: number | null;
  notas?: string | null;
}): Promise<Paca> {
  return apiFetch<Paca>('/api/tienda/pacas', { method: 'POST', body });
}
/** PASO 1: calcula y no escribe nada. */
export function calcularReparto(id: string, pesos?: Record<string, number>): Promise<Reparto> {
  return apiFetch<Reparto>(`/api/tienda/pacas/${id}/reparto/calcular`, {
    method: 'POST',
    body: { pesos },
  });
}
/** PASO 2: escribe el costo y cierra la paca. */
export function aplicarReparto(id: string, pesos?: Record<string, number>): Promise<Reparto> {
  return apiFetch<Reparto>(`/api/tienda/pacas/${id}/reparto/aplicar`, {
    method: 'POST',
    body: { pesos },
  });
}
export function rendimientoDePacas(): Promise<RendimientoDePaca[]> {
  return apiFetch<RendimientoDePaca[]>('/api/tienda/pacas-rendimiento');
}

export function marcarCobrada(id: string, cobrado = true): Promise<Venta> {
  return apiFetch<Venta>(`/api/tienda/ventas/${id}/cobrar`, { method: 'POST', body: { cobrado } });
}
export function porCobrar(): Promise<VentaEnLista[]> {
  return apiFetch<VentaEnLista[]>('/api/tienda/por-cobrar');
}

export function listarGastos(query: {
  tipo?: TipoDeGasto;
  soloPendientes?: boolean;
} = {}): Promise<Gasto[]> {
  return apiFetch<Gasto[]>('/api/tienda/gastos', { query });
}
export function crearGasto(body: {
  concepto: string;
  monto: number;
  tipo?: TipoDeGasto;
  categoria?: string | null;
  pacaId?: string | null;
  aplicaItbis?: boolean;
}): Promise<Gasto> {
  return apiFetch<Gasto>('/api/tienda/gastos', { method: 'POST', body });
}
export function actualizarGasto(id: string, body: Partial<Gasto>): Promise<Gasto> {
  return apiFetch<Gasto>(`/api/tienda/gastos/${id}`, { method: 'PATCH', body });
}

export function bitacora(query: {
  soloAgente?: boolean;
  limit?: number;
} = {}): Promise<EntradaDeBitacora[]> {
  return apiFetch<EntradaDeBitacora[]>('/api/tienda/bitacora', { query });
}

export function propuestas(): Promise<PropuestaDelAgente[]> {
  return apiFetch<PropuestaDelAgente[]>('/api/tienda/propuestas');
}

/**
 * Responder NO ejecuta la accion: la firma y la saca de la bandeja. Quien
 * aprueba va a la venta y la resuelve con el boton de siempre.
 */
export function responderPropuesta(
  id: string,
  estado: 'aprobada' | 'descartada',
): Promise<PropuestaDelAgente> {
  return apiFetch<PropuestaDelAgente>(`/api/tienda/propuestas/${id}/responder`, {
    method: 'POST',
    body: { estado },
  });
}

// ─── Fotos del producto ────────────────────────────────────────────────────

/**
 * De clave a URL servible. El servidor las sirve en `GET /media/*`.
 *
 * Se arma aquí y no se guarda: si mañana el almacenamiento se muda, cambia
 * esta línea y no las cincuenta filas de la base.
 */
export function urlDeFoto(key: string): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
  return `${base}/media/${key}`;
}

export function subirFotoDeProducto(
  id: string,
  archivo: File,
): Promise<{ fotos: string[]; subida: string }> {
  const form = new FormData();
  form.append('foto', archivo);
  // `apiFetch` detecta el FormData y NO le pone Content-Type: el navegador
  // tiene que escribirlo él para incluir el boundary del multipart.
  return apiFetch<{ fotos: string[]; subida: string }>(`/api/productos/${id}/fotos`, {
    method: 'POST',
    body: form,
  });
}

export function quitarFotoDeProducto(id: string, key: string): Promise<{ fotos: string[] }> {
  return apiFetch<{ fotos: string[] }>(`/api/productos/${id}/fotos`, {
    method: 'DELETE',
    body: { key },
  });
}

// ─── De qué anuncio vino ───────────────────────────────────────────────────

export interface VentasDeUnAnuncio {
  /** `null` = no vino de un anuncio. Es el orgánico, y suele ser lo más grande. */
  sourceId: string | null;
  titulo: string | null;
  ventas: number;
  vendido: number;
  /** `vendido − costo conocido`, la misma cuenta que el «margen bruto». */
  margen: number;
  /** Piezas de estas ventas sin costo cargado. Su margen no se puede calcular. */
  piezasSinCosto: number;
}

export function ventasPorAnuncio(
  periodo: Periodo,
  rango: Rango = {},
): Promise<VentasDeUnAnuncio[]> {
  return apiFetch<VentasDeUnAnuncio[]>('/api/tienda/por-anuncio', {
    query: { periodo, ...rango },
  });
}
